import googleTagManager from "@analytics/google-tag-manager"
import { Analytics } from "analytics"

export type SelectedComponentsForAnalytics = {
  modelName: string
  modelId: number
  totalPrice: number
  detailingName: string
  colorName: string
  upholsteryName: string
  accessories: Array<{ name: string; id: string; price: number }>
}

// GTM_ID = "GTM-T932R57"

const GTM_ID = process.env.GTM_ID
if (!GTM_ID) {
  throw new Error("Please provide the GTM_ID environment variable.")
}

/* Initialize analytics? & load plugins */
let analytics

export const initAnalytics = () => {
  analytics = Analytics({
    app: "Mazda Ecommerce",
    plugins: [googleTagManager({ containerId: GTM_ID })],
  })
}

export interface PageTrackingParams {
  path: string
  fullPath: string
  url: string
  title: string
  referrer: string
  hash: string
  queryParams: Record<string, string>
  trackedAt: string
}

const shouldLogTracking = process.env.ENV !== "production"

export const trackPage = (params?: PageTrackingParams) => {
  if (shouldLogTracking) {
    console.info("[analytics] page_track", params || {})
  }

  if (params) {
    return analytics?.page(params)
  }

  return analytics?.page()
}

export const trackImpressions = (models: Array<{ name: string; id: number }>) =>
  analytics?.track("eec.impressions", {
    ecommerce: {
      currencyCode: "ILS",
      impressions: models.map(({ name, id }) => ({ name, id })),
    },
  })

export const trackProductClick = (name: string, id: number) =>
  analytics?.track("productClick", {
    ecommerce: {
      click: {
        actionField: { list: "Homepage" },
        products: [{ name, id }],
      },
    },
  })

export const trackDetail = (name: string, id: number) =>
  analytics?.track("eec.detail", {
    ecommerce: {
      detail: { products: [{ name, id }] },
    },
  })

const buildAnalyticsProducts = ({
  modelName,
  modelId,
  totalPrice,
  detailingName,
  colorName,
  upholsteryName,
  accessories,
}: SelectedComponentsForAnalytics) => [
  {
    name: modelName,
    id: modelId,
    price: totalPrice,
    variant: `${colorName} + ${upholsteryName}`,
    quantity: "1",
    dimension1: detailingName,
  },
  ...accessories,
]

export const trackAddToCart = (components: SelectedComponentsForAnalytics) =>
  analytics?.track("addToCart", {
    ecommerce: {
      currencyCode: "ILS",
      add: {
        products: buildAnalyticsProducts(components),
      },
    },
  })

export const trackCheckoutStepWithComponents = (
  components: SelectedComponentsForAnalytics
) =>
  analytics?.track("checkoutStep", {
    ecommerce: {
      checkout: {
        actionField: { step: "1" },
        products: buildAnalyticsProducts(components),
      },
    },
  })

export const trackCheckoutStep = (step: "2" | "3" | "4" | "5") =>
  analytics?.track("checkoutStep", {
    ecommerce: {
      checkout: {
        actionField: { step },
      },
    },
  })

export const trackTransaction = (
  components: SelectedComponentsForAnalytics,
  orderId: number
) =>
  analytics?.track("transaction", {
    ecommerce: {
      purchase: {
        actionField: {
          id: orderId,
          revenue: components.totalPrice,
        },
        products: buildAnalyticsProducts(components),
      },
    },
  })

export const trackLoggedIn = () => analytics?.track("logged-in")

export const trackCallMeBack = () => analytics?.track("callmeBack")

export const trackPopUpOpen = (name: string) => {
  analytics?.track("popup_showen", {
    popup_name: name,
  })
}

export const trackPopUpClicks = (name: string, field: string) => {
  analytics?.track("popup_field_clicks", {
    popup_name: name,
    field_name: field,
  })
}

export const trackSuccessLead = (name: string, eventName: string) => {
  analytics?.track(eventName, {
    popup_name: name,
  })
}

export const trackSuccessDownload = (name: string, eventName: string) => {
  analytics?.track(eventName, {
    popup_name: name,
  })
}

export const trackMenuClicks = () => analytics?.track("menu_clicks")

export const trackFooterClicks = () => analytics?.track("footer_clicks")

export const trackModelSliderHpClicks = () =>
  analytics?.track("model_slider_hp_clicks")

export const trackPlayWithTheColorClicks = () =>
  analytics?.track("play_with_the_color_clicks")

export const trackHpCubesClicks = () => analytics?.track("hp_cubes_clicks")

export const trackModelsPageClicks = () =>
  analytics?.track("models_click_models_page")

export const trackColorSliderModelPageClicks = () =>
  analytics?.track("color_slider_model_page")

export const trackImageSliderModelPage = () =>
  analytics?.track("image_slider_model_page")

export const trackEngineModelPageClicks = () =>
  analytics?.track("engine_clicks_model_page")

export const trackSubmenuModelPageClicks = () =>
  analytics?.track("submenu_clicks_model_page")

export const trackSpinModelPageClicks = () =>
  analytics?.track("spin_clicks_model_page")

export const trackWhatsupShowroomPageClicks = () =>
  analytics?.track("spin_clicks_model_page")

export const trackPhoneShowroomPageClicks = () =>
  analytics?.track("phone_clicks_showroom_page")

export const trackBuildMazdaCTA = () => {
  analytics?.track("build_your_mazda_cta")
}

export const trackBuildMazdaPopUp = () => {
  analytics?.track("build_your_mazda_popup")
}

export const trackSuccessContacUs = (type: string, eventName: string) => {
  analytics?.track(eventName, {
    lead_type: type,
  })
}

export const trackSkipStoryOne = () => {
  analytics?.track("skip_story_arrow_1")
}

export const trackSkipStoryTwo = () => {
  analytics?.track("skip_story_arrow_1")
}

export const trackServiceCenterPhoneClick = (
  popupName: string,
  serviceCenter: string,
  eventName: string
) => {
  analytics?.track(eventName, {
    popup_name: popupName,
    service_center_name: serviceCenter,
  })
}

export const trackPersonalDetails = (
  eventName:
    | "Meeting"
    | "ContactUs"
    | "TestDrive"
    | "Calculator"
    | "Purchase_leed",
  email: string,
  phone: string,
  isPurchase: boolean
) => {
  analytics?.track(`Mazda_${eventName}${isPurchase ? "_Purchase" : ""}`, {
    email: email,
    phone: phone,
  })
}
